import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Drawer } from '@mui/material';
import { closeViewDialog, fetchPrimaryListData, fetchViews, fetchQuickLinkContacts, setViewAndUserConfigReady } from '../ducks/actions';
import { getIsDialogOpen, getDialogType, getSiteList, getViewManagementListApiUrl, getViewManagementApiUrl, getSaveViewCount, getQuickLinkContactsApiUrl, getUpdateViewCount, getDeleteViewCount, getViewsInfo, getIsViewAndUserConfigReady } from '../ducks/selectors';
import { DIALOG_TYPES } from '../ducks/constants';

// Import dialog content components
import ViewEditContent from './dialogs/ViewEditContent';
import SaveViewAsContent from './dialogs/SaveViewAsContent';
import RenameViewContent from './dialogs/RenameViewContent';
import DeleteViewContent from './dialogs/DeleteViewContent';
import { getUserConfig, getUserInfoUrl } from 'modules/cbre-flow-api-ducks';
import { getUserAccess } from 'modules/cbre-flow-api-ducks/selectors';

interface ViewDialogProps {
  apiUrl: string;
}

const ViewDialog: React.FC<ViewDialogProps> = ({ apiUrl }) => {
  const dispatch = useDispatch();
  const isDialogOpen = useSelector(getIsDialogOpen);
  const dialogType = useSelector(getDialogType);
  const siteList = useSelector(getSiteList)
  const listUrl = useSelector(getViewManagementListApiUrl);
  const fetchViewUrl = useSelector(getViewManagementApiUrl);
  const saveViewCount = useSelector(getSaveViewCount);
  const defaultViewUpdateCount = useSelector(getUpdateViewCount);
  const deleteViewCount = useSelector(getDeleteViewCount);
  const viewInfo = useSelector(getViewsInfo);
  const userInfoUrl = useSelector(getUserInfoUrl);
  const isViewAndUserConfigReady = useSelector(getIsViewAndUserConfigReady);
  
  const quickLinkContactsUrl = useSelector(getQuickLinkContactsApiUrl);
  const { client, role, hasCurrentViewAccess } = useSelector(getUserAccess);

  const allCounts = saveViewCount + deleteViewCount;

  

  useEffect(() => {
    const isUserConfigSuccess = (response: any) => {
      if (!response) {
        return false;
      }
      if (typeof response === 'object' && 'code' in response) {
        return response.code === 200;
      }
      return true;
    };

    const loadViewAndUserConfig = async () => {
      if (!fetchViewUrl || !role?.translationKey || !userInfoUrl) {
        dispatch(setViewAndUserConfigReady(false));
        return;
      }

      dispatch(setViewAndUserConfigReady(false));

      if(!hasCurrentViewAccess) return;

      try {
        const [viewResponse, userConfigResponse] = await Promise.all([
          (dispatch as any)(fetchViews({ apiUrl: `${fetchViewUrl}` })),
          (dispatch as any)(getUserConfig({ apiUrl: `${userInfoUrl}` })),
        ]);

        const isViewSuccess = viewResponse?.statusCode === 200;
        dispatch(setViewAndUserConfigReady(isViewSuccess && isUserConfigSuccess(userConfigResponse)));
      } catch (error) {
        dispatch(setViewAndUserConfigReady(false));
      }
    };

    loadViewAndUserConfig();
  }, [fetchViewUrl, userInfoUrl, dispatch, allCounts, role?.translationKey, defaultViewUpdateCount]);

  useEffect(() => {
    if(!role?.translationKey || !viewInfo?.clientCode || !isViewAndUserConfigReady) return;
    const apiUrl = `${listUrl}&clientCode=${viewInfo.clientCode}&searchFrom=GLOBAL`;
    dispatch(fetchPrimaryListData(apiUrl, 'allLists'));
    dispatch(fetchQuickLinkContacts(quickLinkContactsUrl));
    
  }, [dispatch, viewInfo?.clientCode, listUrl, quickLinkContactsUrl, role?.translationKey, allCounts, isViewAndUserConfigReady]);

  const handleClose = () => {
    dispatch(closeViewDialog());
  };

  return (
    <>
      {/* Render EDIT dialog type in Drawer */}
      {dialogType === DIALOG_TYPES.EDIT && (
        <Drawer anchor="right" open={isDialogOpen} onClose={handleClose} ModalProps={{ keepMounted: false }} style={{ zIndex: 1250 }}
          PaperProps={{
            sx: {
              width: { xs: '100vw', sm: '520px' },
              maxWidth: '100vw'
            }
          }}
        >
          <ViewEditContent
            onClose={handleClose}
            apiUrl={apiUrl}
          />
        </Drawer>
      )}

      {/* Render other dialog types as Modals */}
      {dialogType === DIALOG_TYPES.SAVE_AS && (
        <SaveViewAsContent
          onClose={handleClose}
          open={isDialogOpen}
          selectedClient={client}
        />
      )}

      {dialogType === DIALOG_TYPES.RENAME && (
        <RenameViewContent
          onClose={handleClose}
          open={isDialogOpen}
        />
      )}

      {dialogType === DIALOG_TYPES.DELETE && (
        <DeleteViewContent
          onClose={handleClose}
          open={isDialogOpen}
          selectedClient={client}
        />
      )}
    </>
  );
};

export default ViewDialog;